import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Globe, Languages, MapPin, Tag } from "lucide-react";
import { InstagramIcon } from "../components/SocialIcons";
import Seo from "../components/Seo";
import TeacherScheduleBlock from "../components/TeacherScheduleBlock";
import CertificationsSection from "../components/CertificationsSection";
import PhotoGallery from "../components/PhotoGallery";
import { fetchTeacherBySlug } from "../services/api";
import { getOfferingListingPath } from "../lib/offeringRoutes";
import { formatScheduleCompact } from "../lib/scheduleDisplay";
import { buildTeacherScheduleLines, formatOfferingPriceLabel } from "../lib/teacherScheduleLines";
import { getTeacherImage } from "../lib/teacherImages";

function TeacherProfilePage() {
  const { slug } = useParams();
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    fetchTeacherBySlug(slug)
      .then((data) => setTeacher(data))
      .catch(() => setError("Could not load teacher profile. Please try again."))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (error || !teacher) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <p className="text-gray-600 mb-4">{error || "Teacher not found."}</p>
          <Link to="/online-courses" className="text-emerald-600 hover:text-emerald-700 font-semibold inline-flex items-center gap-1">
            <ArrowLeft size={16} /> Back to Online Courses
          </Link>
        </div>
      </div>
    );
  }

  const location = [teacher.city, teacher.country].filter(Boolean).join(", ") || "India";
  const offerings = Array.isArray(teacher.offerings) ? teacher.offerings : [];
  const onlineOfferings = offerings.filter((item) => item.listing_type === "Online Course");
  const prenatalOfferings = offerings.filter((item) => item.listing_type === "Prenatal Yoga");
  const allScheduleLines = buildTeacherScheduleLines([...onlineOfferings, ...prenatalOfferings], {
    includePrices: true,
  });
  const expertise = teacher.expertise
    ? teacher.expertise.split("|").map((item) => item.trim()).filter(Boolean)
    : [];
  const languages = teacher.languages
    ? String(teacher.languages)
        .split(/[|,]/)
        .map((item) => item.trim())
        .filter(Boolean)
    : [];
  const teacherPhoto = getTeacherImage({
    teacher_slug: teacher.slug,
    teacher_id: teacher.teacher_id,
    profile_image: teacher.profile_image,
  });
  const galleryImages = Array.isArray(teacher.gallery_images) ? teacher.gallery_images : [];

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <Seo
        title={`${teacher.teacher_name} | Yoga Teacher`}
        description={teacher.headline || `${teacher.teacher_name} teaches online yoga classes in ${location}.`}
        canonicalPath={`/teachers/${slug}`}
        ogImagePath={teacherPhoto}
      />

      <div className="relative overflow-hidden bg-gradient-to-br from-emerald-900 via-teal-900 to-gray-900 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.12),transparent_45%)]" />
        <div className="relative max-w-5xl mx-auto px-4 py-10 md:py-14">
          <Link to="/online-courses" className="inline-flex items-center gap-1 text-sm text-emerald-100 hover:text-white mb-8">
            <ArrowLeft size={14} /> Back to courses
          </Link>

          <div className="flex flex-col md:flex-row items-center md:items-end gap-8">
            <div className="w-36 h-36 md:w-44 md:h-44 rounded-3xl overflow-hidden bg-white/10 ring-4 ring-white/20 shadow-2xl shrink-0">
              <img src={teacherPhoto} alt={teacher.teacher_name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 text-center md:text-left pb-2">
              <p className="text-emerald-200 text-xs font-bold uppercase tracking-[0.25em] mb-3">
                Yoga Teacher
              </p>
              <h1 className="text-4xl md:text-6xl font-black tracking-tight leading-none mb-4">
                {teacher.teacher_name}
              </h1>
              {teacher.headline && (
                <p className="text-emerald-50/90 text-base md:text-lg leading-relaxed max-w-2xl mb-4">
                  {teacher.headline}
                </p>
              )}
              <p className="flex items-center justify-center md:justify-start gap-2 text-sm text-emerald-100/90 mb-5">
                <MapPin size={15} className="text-emerald-300" />
                {location}
              </p>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4">
                {teacher.website && (
                  <a href={teacher.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/90 hover:text-white">
                    <Globe size={15} /> Website
                  </a>
                )}
                {teacher.instagram && (
                  <a href={teacher.instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/90 hover:text-white">
                    <InstagramIcon size={15} /> Instagram
                  </a>
                )}
              </div>
              {galleryImages.length > 0 && (
                <div className="mt-6 flex flex-wrap justify-center md:justify-start gap-2">
                  {galleryImages.slice(0, 4).map((img, index) => {
                    const url = typeof img === "string" ? img : img?.url || img?.public_url;
                    if (!url) return null;
                    return (
                      <div
                        key={`${url}-${index}`}
                        className="h-14 w-14 overflow-hidden rounded-xl ring-2 ring-white/25"
                      >
                        <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10 space-y-8">
        <section>
          <TeacherScheduleBlock lines={allScheduleLines} />
        </section>

        {teacher.bio && (
          <section className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
            <h2 className="text-xl font-black text-gray-900 mb-4">About</h2>
            <p className="text-gray-600 leading-relaxed whitespace-pre-line">{teacher.bio}</p>
          </section>
        )}

        {galleryImages.length > 0 && (
          <section className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
            <h2 className="text-xl font-black text-gray-900 mb-5">Gallery</h2>
            <PhotoGallery images={galleryImages} />
          </section>
        )}

        {languages.length > 0 && (
          <section className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
            <h2 className="text-xl font-black text-gray-900 mb-4 flex items-center gap-2">
              <Languages size={18} className="text-emerald-500" /> Languages
            </h2>
            <div className="flex flex-wrap gap-2">
              {languages.map((item) => (
                <span
                  key={item}
                  className="bg-teal-50 text-teal-800 text-sm font-semibold px-3 py-1 rounded-full border border-teal-100"
                >
                  {item}
                </span>
              ))}
            </div>
          </section>
        )}

        {expertise.length > 0 && (
          <section className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
            <h2 className="text-xl font-black text-gray-900 mb-4 flex items-center gap-2">
              <Tag size={18} className="text-emerald-500" /> Expertise
            </h2>
            <div className="flex flex-wrap gap-2">
              {expertise.map((item) => (
                <span key={item} className="bg-emerald-50 text-emerald-700 text-sm font-semibold px-3 py-1 rounded-full border border-emerald-100">
                  {item}
                </span>
              ))}
            </div>
          </section>
        )}

        <CertificationsSection certifications={teacher.certifications} />

        {onlineOfferings.length > 0 && (
          <OfferingGroup title="Online Courses" offerings={onlineOfferings} />
        )}

        {prenatalOfferings.length > 0 && (
          <OfferingGroup title="Prenatal Yoga Classes" offerings={prenatalOfferings} />
        )}
      </div>
    </div>
  );
}

function OfferingGroup({ title, offerings }) {
  return (
    <section className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
      <h2 className="text-xl font-black text-gray-900 mb-4">{title}</h2>
      <div className="space-y-3">
        {offerings.map((offering) => {
          const base = getOfferingListingPath(offering.listing_type);
          const schedule = formatScheduleCompact(offering.schedule_json, offering.schedule_summary);
          const priceLabel = formatOfferingPriceLabel(offering);
          return (
            <Link
              key={offering.offering_key}
              to={`${base}/${offering.slug}`}
              className="block rounded-2xl border border-gray-100 px-5 py-4 hover:border-emerald-200 hover:bg-emerald-50/40 transition-colors"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <p className="font-bold text-gray-900">{offering.title}</p>
                  {schedule && (
                    <p className="text-sm text-gray-600 flex items-center gap-2 mt-1.5">
                      <Clock size={14} className="text-emerald-600 shrink-0" />
                      {schedule}
                    </p>
                  )}
                  {offering.duration && (
                    <p className="text-xs text-gray-400 mt-1">{offering.duration}</p>
                  )}
                </div>
                {priceLabel ? (
                  <p className="text-sm font-semibold text-emerald-700">{priceLabel}</p>
                ) : null}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

export default TeacherProfilePage;
